import React, { useEffect, useState } from "react";
import { PolarProductTypes } from "../../../src/constants/PolarProductTypes";
import { LanguageCode, LanguageNameMap } from "../../../src/constants/LanguageCode";
import { CategoryJsonModel, CategoryPackageJsonModel, LanguageJsonModel, ProbabilityTableModel } from "../../models/BlockTransportSystemJsonModel";
import { convertDatetime, getDateWithoutSeconds } from "../../utils/DateHelper";
import GlobalEnvironments from "../../../src/constants/GlobalEnvironments";
import { Modal, Button, Stack, Alert, ToggleButton, Form, Row, Col, Table, Image, FloatingLabel, Spinner } from "react-bootstrap";
import { KioskTypes, PolarProductConfig, ProductCategoryTypes, productConfigs } from "../../constants/PolarProductList";
import { PresetConfig, presetConfigs, PresetGroups, PresetTypes } from "../../constants/PresetConfig";
import { inject, observer } from "mobx-react";
import BlockKitStore from "../../stores/BlockKitStore";
import { UserPermissionModel } from "../../controllers/StudioAdminController";
import StudioUserStore from "../../stores/StudioUserStore";
import { useTranslation } from "react-i18next";

interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
  isShow: boolean;
  isCreateMode: boolean;
  category?: CategoryJsonModel;
  categoryPackages?: CategoryPackageJsonModel[];
  productListItems?: PolarProductConfig[];
  timeZone?: string;
  allowedPresets: string[];
  onClickApply?: (categry: CategoryJsonModel, file?: File) => Promise<void>;
  onClickClose: () => void;
}

const CategoryInfoCard: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore,
  isShow,
  isCreateMode,
  category,
  categoryPackages,
  timeZone,
  allowedPresets,
  onClickApply,
  onClickClose,
}: IProps) => {
  // 초기값을 별도로 저장
  const initialProbabilities: ProbabilityTableModel[] = [
    {
      tableType: "single",
      probabilities: [
        { grade: 1, probability: 0.5 },
        { grade: 2, probability: 0.3 },
        { grade: 3, probability: 0.15 },
        { grade: 4, probability: 0.04 },
        { grade: 5, probability: 0.01 },
      ],
    },
    {
      tableType: "triple",
      probabilities: [
        { grade: 1, probability: 0.2 },
        { grade: 2, probability: 0.2 },
        { grade: 3, probability: 0.3 },
        { grade: 4, probability: 0.2 },
        { grade: 5, probability: 0.1 },
      ],
    },
  ];

  const [thumbnailFile, setThumbnailFile] = useState<File>();
  const [thumbnailLocalUrl, setThumbnailLocalUrl] = useState<string>();
  const [isChangedThumbnail, setIsChangedThumbnail] = useState<boolean>();
  const [inputs, setInputs] = useState({
    lang: category?.lang ?? [],
    releaseDate: category?.releaseDate ?? getDateWithoutSeconds(),
    expireDate: category?.expireDate ?? new Date(2100, 11, 31),
    priority: category?.priority ?? 3,
    dedicatedProducts: category?.dedicatedProducts ?? [],
    packageId: category?.packageId ?? "",
  });

  const [probabilityType, setProbabilityType] = useState<string>(category?.probabilityTables ? "random" : "normal");
  const [probabilityTables, setProbabilityTables] = useState<ProbabilityTableModel[]>(
    category?.probabilityTables ?? initialProbabilities);
  const [isInvalidProbability, setIsInvalidProbability] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [presetGroup, setPresetGroup] = useState<PresetGroups>();
  const [presetType, setPresetType] = useState<PresetTypes>();
  const [presetConfig, setPresetConfig] = useState<PresetConfig>();
  const [productCategory, setProductCategory] = useState<ProductCategoryTypes>();
  const [kioskType, setKioskType] = useState<KioskTypes>();
  const [filtereProductConfigs, setFilteredProductConfigs] = useState<PolarProductConfig[]>();
  const [permissions, setPermissions] = useState<UserPermissionModel[]>([]);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const { t } = useTranslation();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setInputs({
      ...inputs,
      [name]: (name === "releaseDate" || name === "expireDate")
        ? new Date(value)
        : value,
    });
  };

  const onChangeLanguageField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "name" | "description"
  ) => {
    const { value, name } = e.target;
    const findLang =
      inputs.lang?.find((i) => i.key === name) ?? ({ key: name } as LanguageJsonModel);

    setInputs({
      ...inputs,
      lang: [
        ...inputs.lang?.filter((i) => i.key !== name),
        {
          ...findLang,
          [field]: value,
        },
      ],
    });
  };

  const handleThumbnailFileOnChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let reader = new FileReader();
    if (e.target.files) {
      let file = e.target.files[0];
      reader.onloadend = () => {
        const base64 = reader.result;
        if (base64) {
          setThumbnailFile(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // 확률 변경 시 바로 반영 + 검증
  const handleProbabilityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    ticketType: string
  ) => {
    const newProbabilities = probabilityTables?.map((probability) =>
      probability.tableType === ticketType
        ? {
          ...probability,
          probabilities: probability.probabilities.map((p, i) =>
            i === index ? { ...p, probability: parseFloat(e.target.value) / 100 } : p
          ),
        }
        : probability
    );

    setProbabilityTables(newProbabilities);

    // 변경 직후 합계 검증
    const invalid = newProbabilities?.some((p) => {
      const total = p.probabilities.reduce((sum, g) => sum + g.probability, 0);
      return Math.round(total * 100) / 100 !== 1;
    });

    setIsInvalidProbability(invalid);
    setError(invalid ? "확률의 합이 100%가 아닙니다." : "");
  };

  useEffect(() => {
    if (thumbnailFile) {
      if (thumbnailLocalUrl) {
        URL.revokeObjectURL(thumbnailLocalUrl);
      }

      setThumbnailLocalUrl(URL.createObjectURL(thumbnailFile));
    }
  }, [thumbnailFile]);

  useEffect(() => {
    if (category?.id) {
      blockKitStore?.studioController.getEntityPermissionsAsync("categories", category.id).then(setPermissions);
    }

    return () => {
      if (thumbnailLocalUrl) {
        URL.revokeObjectURL(thumbnailLocalUrl);
        setThumbnailLocalUrl("");
      }
    };
  }, []);

  useEffect(() => {
    if (thumbnailLocalUrl) {
      URL.revokeObjectURL(thumbnailLocalUrl);
      setThumbnailLocalUrl("");
    }
    setThumbnailFile(undefined);
    setInputs({
      ...inputs,
      releaseDate: category?.releaseDate ?? getDateWithoutSeconds(),
      expireDate: category?.expireDate ?? new Date(2100, 11, 31),
      priority: category?.priority ?? 3,
      lang: category?.lang ?? [],
      packageId: category?.packageId ?? "",
      dedicatedProducts: category?.dedicatedProducts || []
    });

    if (category?.probabilityTables && category?.probabilityTables.length > 0) {
      setProbabilityType("random"); // 확률 정보가 있으면 타입을 random 설정
      setProbabilityTables(category?.probabilityTables);
    } else {
      setProbabilityType("normal");
      setProbabilityTables(initialProbabilities);
    }
  }, [category]);

  useEffect(() => {
    if (presetGroup && presetType) {
      const config = presetConfigs.find(i => i.groups.includes(presetGroup) && i.type === presetType);
      setPresetConfig(config);
    } else {
      setPresetConfig(undefined);
    }
  }, [presetGroup, presetType])

  useEffect(() => {
    const configs = productConfigs
      ?.filter(i => (!kioskType || i.kioskTypes.includes(kioskType)) && (!productCategory || i.productCategory === productCategory))
      ?.sort()
    setFilteredProductConfigs(configs);
  }, [kioskType, productCategory])

  const getThumbnail = () => {
    return category?.thumbnailUrl ? `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${category?.thumbnailUrl}` : "";
  };

  return (
    <Modal show={isShow} size="lg" onHide={() => onClickClose()}>
      <Modal.Header closeButton>{t("category")}</Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Form.Group as={Row} className="mb-1" hidden={!isCreateMode}>
          <Col sm={4}>
            <Form.Select
              value={presetGroup}
              onChange={(e) => setPresetGroup(e.target.value as PresetGroups)}
            >
              <option value="" key="none">{t("preset-group-selection")}</option>
              {allowedPresets?.map((preset) => (
                <option value={preset} key={preset}>
                  {preset}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={8}>
            <Form.Select
              value={presetType}
              onChange={(e) => {
                setPresetType(e.target.value as PresetTypes);
                const dedicatedProducts = presetConfigs.find(i => i.type === e.target.value)?.dedicatedProducts;
                if (dedicatedProducts) {
                  setInputs({ ...inputs, dedicatedProducts });
                }
              }}
              disabled={!presetGroup}
            >
              <option value="" key="none">{t("preset-selection")}</option>
              {presetConfigs
                ?.filter(i => presetGroup && i.groups.includes(presetGroup))
                ?.map((preset) => (
                  <option value={preset.type} key={preset.type}>
                    {preset.type}
                  </option>
                ))}
            </Form.Select>
          </Col>
        </Form.Group>
        <Form.Group hidden={isCreateMode && !presetConfig}>
          <Form.Group className="mb-1" hidden={isCreateMode && presetConfig?.isThumbnailConfigurable === false}>
            <Row className="align-items-center">
              <Col sm={3}>
                <Form.Label>{t("thumbnail")}</Form.Label>
              </Col>
              <Col sm={5}>
                <a
                  href={thumbnailLocalUrl ?? getThumbnail()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    const url = thumbnailLocalUrl ?? getThumbnail();
                    if (!url) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Image
                    src={thumbnailLocalUrl ?? getThumbnail()}
                    alt={t("thumbnail")}
                    thumbnail
                    style={{ width: "300px", height: "120px", objectFit: "cover" }}
                  />
                </a>
              </Col>
              <Col sm={4}>
                <Form.Control
                  id="thumbnail"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    e.preventDefault();
                    handleThumbnailFileOnChange(e as React.ChangeEvent<HTMLInputElement>);
                    setIsChangedThumbnail(true);
                  }}
                />
                <Form.Label htmlFor="thumbnail" className="btn btn-outline-primary">{t("select-file")}</Form.Label>
              </Col>
            </Row>
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={isCreateMode}>
            <Form.Label column sm={3}>{t("id")}</Form.Label>
            <Col><Form.Control value={category?.id} readOnly /></Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={isCreateMode}>
            <Form.Label column sm={3}>{t("name")}</Form.Label>
            <Col><Form.Control value={category?.name} readOnly /></Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={isCreateMode && !presetConfig?.isPriorityConfigurable}>
            <Form.Label column sm={3}>{t("priority")}</Form.Label>
            <Col>
              <Form.Control
                type="number"
                min={1}
                value={inputs.priority ?? ""}
                onChange={(e) => {
                  setInputs({ ...inputs, priority: e.target.value === "" ? 3 : +e.target.value });
                }}
              />
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-1">
            <Form.Label column sm={3}>{t("release-date")}</Form.Label>
            <Col>
              <Form.Control
                type={"datetime-local"}
                name={"releaseDate"}
                value={
                  inputs.releaseDate
                    ? convertDatetime(new Date(inputs.releaseDate), timeZone)
                    : ""
                }
                onChange={onChange}
              />
            </Col>
            <Col sm={2}>
              <Form.Label>({timeZone})</Form.Label>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-1">
            <Form.Label column sm={3}>{t("expire-date")}</Form.Label>
            <Col>
              <Form.Control
                type={"datetime-local"}
                name={"expireDate"}
                value={
                  inputs.expireDate
                    ? convertDatetime(new Date(inputs.expireDate), timeZone)
                    : ""
                }
                onChange={onChange}
              />
            </Col>
            <Col sm={2}>
              <Form.Label>({timeZone})</Form.Label>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={isCreateMode && !presetConfig?.isPackageConfigurable}>
            <Form.Label column sm={3}>{t("package-name")}</Form.Label>
            <Col sm={9}>
              <FloatingLabel label={inputs.packageId}>
                <Form.Select
                  value={inputs.packageId}
                  onChange={(e) => setInputs({ ...inputs, packageId: e.target.value ?? "" })}
                >
                  <option value="" key="none">{t("package-none")}</option>
                  {categoryPackages?.map((categoryPackage) => (
                    <option value={categoryPackage.id} key={categoryPackage.id}>
                      {categoryPackage.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Form.Group>
          <Form.Group className="mb-1">
            <Form.Label>{t("multi-language")}</Form.Label>
            {Object.values(LanguageCode)
              .filter((langCode) => !presetConfig || presetConfig.configurableLanguages.includes(langCode))
              .map((langCode) => (
                <Row key={langCode} className="mb-1 align-items-start">
                  {/* 왼쪽 라벨 */}
                  <Col sm={3}>
                    <Form.Label>
                      {LanguageNameMap[langCode] ?? langCode}
                    </Form.Label>
                  </Col>

                  {/* 오른쪽 입력 영역 */}
                  <Col sm={9}>
                    <Form.Control
                      type="text"
                      name={langCode}
                      value={inputs?.lang?.find((i) => i.key === langCode)?.name ?? ""}
                      onChange={(e) => onChangeLanguageField(e, "name")}
                      placeholder={t("name")}
                      className="mb-1"
                    />
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name={langCode}
                      value={inputs?.lang?.find((i) => i.key === langCode)?.description ?? ""}
                      onChange={(e) => onChangeLanguageField(e, "description")}
                      placeholder={t("description")}
                    />
                  </Col>
                </Row>
              ))}
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={presetConfig?.isProbabilityConfigurable === false}>
            <Form.Label column sm={3}>{t("probability")}</Form.Label>
            <Col sm={9}>
              <Form.Select
                value={probabilityType}
                onChange={(e) => setProbabilityType(e.target.value)}
              >
                <option value="normal">normal</option>
                <option value="random">random</option>
              </Form.Select>
            </Col>
          </Form.Group>
          {/* 확률표를 위한 타입 */}
          <Form.Group hidden={presetConfig?.isProbabilityConfigurable === false || probabilityType !== "random"}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>{t("grade")}</th>
                  <th>{t("probability-1time")}</th>
                  <th>{t("probability-3time")}</th>
                </tr>
              </thead>
              <tbody>
                {[`⭐️ 1${t("star-rating")}`, `⭐️ 2${t("star-rating")}`, `⭐️ 3${t("star-rating")}`, `⭐️ 4${t("star-rating")}`, `⭐️ 5${t("star-rating")}`].map((grade, index) => (
                  <tr key={index}>
                    <td>{grade}</td>
                    <td>
                      <Form.Control
                        type="number"
                        value={
                          (probabilityTables?.find((p) => p.tableType === "single")
                            ?.probabilities[index]?.probability || 0) * 100
                        }
                        onChange={(e) => handleProbabilityChange(e, index, "single")}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={
                          (probabilityTables?.find((p) => p.tableType === "triple")
                            ?.probabilities[index]?.probability || 0) * 100
                        }
                        onChange={(e) => handleProbabilityChange(e, index, "triple")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {error && <Alert className="m-1" variant="danger">{error}</Alert>}
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={isCreateMode && (presetConfig?.isDedicatedProductsConfigurable !== true || !presetGroup || !presetType)}>
            <Form.Label>{t("dedicated-product")}</Form.Label>
            <Col sm={3}>
              <Form.Select
                value={kioskType}
                onChange={(e) => setKioskType(e.target.value as KioskTypes)}
              >
                <option value="" key="none">{t("kiosk-type-selection")}</option>
                {Object.values(KioskTypes)?.map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={9} className="mb-1">
              <Form.Select
                value={productCategory}
                onChange={(e) => setProductCategory(e.target.value as ProductCategoryTypes)}
              >
                <option value="" key="none">{t("product-category-selection")}</option>
                {[...new Set(
                  productConfigs
                    ?.filter(i => kioskType && i.kioskTypes.includes(kioskType))
                    ?.map(product => product.productCategory)
                )]
                  .map(category => (
                    <option value={category} key={category}>
                      {category}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Stack direction="horizontal" gap={1} className="flex-wrap">
              {filtereProductConfigs?.map((value, index) => {
                return (
                  <ToggleButton
                    variant={"outline-secondary"}
                    size="sm"
                    type="checkbox"
                    id={value.productType}
                    value={value.productType}
                    checked={inputs?.dedicatedProducts?.includes(value.productType)}
                    onClick={() => {
                      const copiedProduct = [...inputs.dedicatedProducts];
                      const index = copiedProduct.indexOf(value.productType);
                      if (index < 0) {
                        copiedProduct.push(value.productType);
                        setInputs({ ...inputs, dedicatedProducts: copiedProduct })
                      } else {
                        copiedProduct.splice(index, 1);
                        setInputs({ ...inputs, dedicatedProducts: copiedProduct })
                      }
                    }}
                  >
                    {value.productType}
                  </ToggleButton>
                );
              })}
            </Stack>
          </Form.Group>
          <Form.Group as={Row} className="mb-1" hidden={presetConfig?.isDedicatedProductsConfigurable === true}>
            <Form.Label>{t("dedicated-product")}</Form.Label>
            <Stack direction="horizontal" gap={1} className="flex-wrap">
              {filtereProductConfigs
                ?.filter(value => inputs?.dedicatedProducts?.includes(value.productType))
                ?.map((value, index) => {
                  return (
                    <ToggleButton
                      variant={"outline-secondary"}
                      size="sm"
                      type="checkbox"
                      id={value.productType}
                      value={value.productType}
                      checked={inputs?.dedicatedProducts?.includes(value.productType)}
                    >
                      {value.productType}
                    </ToggleButton>
                  );
                })}
            </Stack>
          </Form.Group>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={async () => {
            setIsWorking(true);
            try {
              if (!confirm(t("save-confirmation-message"))) {
                return;
              }

              if (!inputs.priority || !Number.isInteger(inputs.priority) || inputs.priority <= 0) {
                alert(t("priority-input-message"));
                return;
              }

              if (inputs?.dedicatedProducts?.length === 0) {
                alert(t("dedicated-product-selection-message"));
                return;
              }

              await onClickApply(
                {
                  ...category,
                  ...inputs,
                  name: inputs?.lang.find((i) => i.key === LanguageCode.koKR)?.name ?? "",
                  description: inputs?.lang.find((i) => i.key === LanguageCode.koKR)?.description ?? "",
                  lang: inputs?.lang,
                  dedicatedProducts: inputs?.dedicatedProducts,
                  packageId: inputs.packageId,
                  probabilityTables: probabilityType === "random" ? probabilityTables : [],
                },
                isChangedThumbnail ? thumbnailFile : undefined
              );
            } finally {
              setIsWorking(false);
            }
          }}
          disabled={
            (!isCreateMode && !studioUserStore?.hasWritePermission(permissions)) ||
            isInvalidProbability || (isCreateMode && (!presetGroup || !presetType)) || isWorking}
        >
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-save")}
        </Button>
        <Button disabled={isWorking} variant="secondary" onClick={() => onClickClose()}>
          {t("button-close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}));

export default CategoryInfoCard;